import { defineComponent } from "vue";
import { mapActions } from "pinia";
import { Container } from "aurelia-dependency-injection";

import { Task } from "../../../../models/task.model";
import { ITask } from "../../../../resources-vue/vue-interfaces/i-task";
import { TaskService } from "../../../../services/task.service";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { NavigationService } from "../../../../services/navigation.service";
import { getApplicationId } from "../../../../common/vue-helpers/modifiers/application-modifiers";
import { formatDate, getTaskReturnComments } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { ApplicationEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../../enums/enums";
import { useLoadingIndicator } from "../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { getHospiceRedirectionUrl } from "../../../../common/get-hospice-redirection-url";

import CardTemplate from "../../../../resources-vue/vue-elements/CardTemplate/CardTemplate.vue";
import ProductPill from "../../../../resources-vue/vue-elements/ProductPill/ProductPill.vue";

export default defineComponent({
    components: {
        CardTemplate,
        ProductPill
    },
    props: {
        label: {
            default: "",
            type: String
        }
    },

    data() {
        return {
            _taskService: null,
            _permissionManager: null,
            _analyticsService: null,
            _navigate: null,
            tasksList: [] as Task[],
            isLoading: false,
            hasError: false,
            tooltip: `This section displays all visits and tasks returned by QA.`,
            userOs: "",
            isAscending: true as boolean,
            formatDate,
            getApplicationId,
            getTaskReturnComments
        };
    },
    created() {
        this._taskService = Container.instance.get(TaskService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._navigate = Container.instance.get(NavigationService);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },
    async mounted() {
        this.userOs = window.sessionStorage.getItem("userOs");
        await this.getReturnedByQATasks();
    },
    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        async getReturnedByQATasks() {
            try {
                this.hasError = false;
                this.isLoading = true;
                this.tasksList = await this._taskService.getAlerts();

                let incidentsTaskList: any[] = await this._taskService.getIncidentAlerts();
                let mappedIncidentTaskList: any[] = incidentsTaskList.map((incidentTask) => ({
                    ...incidentTask,
                    patientFullName: incidentTask.PatientName,
                    BranchName: incidentTask.AgencyName,
                    VisitName: incidentTask.Data.IncidentType,
                    IsIncidentLog: true
                }));

                this.tasksList = this.tasksList.concat(mappedIncidentTaskList);
                this.tasksList = this._taskService.sortTasks(this.tasksList, "Date", "date", this.isAscending);
            } catch (e) {
                console.error(e);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async viewPatient(task: ITask) {
            const isClinician = await this._permissionManager.checkPermission(
                ParentPermissionEnum.clinician,
                PermissionActionEnum.canView,
                task.AgencyId
            );
            const analyticsOptions = {
                category: AnalyticsCategory.AxxessPlannerAlerts,
                action: "Planner View patient chart"
            };
            const navParams = {
                applicationId: task.Application,
                agencyId: task.AgencyId,
                userId: task.UserId,
                patientId: task.PatientId,
                isClinician
            };
            if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                window.location.href=
                    `${getHospiceRedirectionUrl(task.AgencyId)}/patients/patient-chart/${
                        task.PatientId
                    }?accountId=${task.AgencyId}`
                ;
            } else {
                try {
                    this.showLoading(true);
                    const response = await this._navigate.navigateToPatient(navParams);
                    this._navigate.redirect(response, analyticsOptions);
                } catch (e) {
                    console.error(e);
                    this.showLoading(false);
                }
            }
        },

        async startTask(task: ITask) {
            try {
                this.showLoading(true);
                const isIncidentLog = task.VisitName.includes("Incident");
                const isInfectionLog = task.VisitName.includes("Infection");
                if (isIncidentLog) {
                    const analyticsOptions = {
                        category: AnalyticsCategory.AxxessPlannerAlerts,
                        action: "Planner Incident log Review visit"
                    };
                    const navParams = {
                        agencyId: task.AgencyId,
                        userId: task.UserId,
                        applicationId: task.Application,
                        patientId: task.PatientId,
                        id: task.Id
                    };

                    let response = await this._navigate.navigateToIncidentLog(navParams);
                    this._navigate.redirect(response, analyticsOptions);
                } else if (isInfectionLog) {
                    const analyticsOptions = {
                        category: AnalyticsCategory.AxxessPlannerAlerts,
                        action: "Planner Infection log Review visit"
                    };
                    const navParams = {
                        agencyId: task.AgencyId,
                        userId: task.UserId,
                        applicationId: task.Application,
                        patientId: task.PatientId,
                        id: task.Id
                    };

                    const response = await this._navigate.navigateToInfectionLog(navParams);
                    this._navigate.redirect(response, analyticsOptions);
                } else {
                    if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                        window.open(
                            `${getHospiceRedirectionUrl(task.AgencyId)}/note/${task.Id}?accountId=${task.AgencyId}`
                        );
                    } else {
                        const analyticsOptions = {
                            category: AnalyticsCategory.AxxessPlannerAlerts,
                            action: "Planner Review Visit"
                        };
                        const navParams = {
                            agencyId: task.AgencyId,
                            userId: task.UserId,
                            applicationId: task.Application,
                            isClinician: true,
                            patientId: task.PatientId,
                            taskId: task.Id,
                            episodeId: task.EpisodeId,
                            locationId: task.LocationId,
                            status: task.PatientStatus ? Number(task.PatientStatus) : null,
                            payerId: task.PayerId,
                            Id: task.Id
                        };

                        let response = await this._navigate.navigateToTask(navParams);
                        this._navigate.redirect(response, analyticsOptions);
                    }
                }
            } catch (e) {
                console.error(e);
                this.showLoading(false);
            }
        },

        sortByScheduleDate(): void {
            this.isAscending = !this.isAscending;
            this.tasksList = this._taskService.sortTasks(this.tasksList, "Date", "date", !this.isAscending);
            this._analyticsService.logEvent({
                category: AnalyticsCategory.AxxessPlannerAlerts,
                action: `${this.isAscending ? "Ascending" : "Descending"}-Sort`
            });
        },
        isHomeCare(application: number) {
            if (application == ApplicationEnum.HomeCare) {
                return true;
            }
            return false;
        },

        getReturnComments(str: string) {
            const comments = str.split(/\r?\n/).filter((ln) => ln.trim());
            return getTaskReturnComments(comments);
        }
    }
});
